import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Settings } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AccountTierModal } from "./AccountTierModal";

interface UserData {
  id: string;
  email: string;
  user_created_at: string;
  first_name: string;
  last_name: string;
  business_name: string;
  user_type: string;
  subscription_active: boolean;
  full_name: string;
  account_tier: string | null;
  admin_tier: string | null;
}

export const UsersSection = () => {
  const [tierModalOpen, setTierModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);

  const {
    data: users,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_all_users_for_admin");

      if (error) {
        throw error;
      }

      return data as UserData[];
    },
  });

  const handleOpenTierModal = (user: UserData) => {
    setSelectedUser(user);
    setTierModalOpen(true);
  };

  const handleCloseTierModal = () => {
    setTierModalOpen(false);
    setSelectedUser(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error loading users data</p>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getSubscriptionStatus = (
    userType: string,
    subscriptionActive: boolean
  ) => {
    if (userType === "buyer" || userType === "admin") {
      return "N/A";
    }
    return subscriptionActive ? "Active" : "Inactive";
  };

  const getSubscriptionBadgeVariant = (
    userType: string,
    subscriptionActive: boolean
  ) => {
    if (userType === "buyer" || userType === "admin") {
      return "secondary";
    }
    return subscriptionActive ? "default" : "destructive";
  };

  const getUserTypeBadgeVariant = (userType: string) => {
    switch (userType) {
      case "admin":
        return "destructive";
      case "breeder":
        return "default";
      case "buyer":
        return "secondary";
      default:
        return "secondary";
    }
  };

  const getUserTypeLabel = (userType: string) => {
    switch (userType) {
      case "admin":
        return "Admin";
      case "breeder":
        return "Breeder";
      case "buyer":
        return "Buyer";
      default:
        return userType;
    }
  };

  const getTierLabel = (tier: string | null) => {
    switch (tier) {
      case "freemium":
        return "Freemium";
      case "standard":
      default:
        return "Standard";
    }
  };

  const getTierBadgeVariant = (tier: string | null) => {
    switch (tier) {
      case "freemium":
        return "secondary";
      case "standard":
      default:
        return "outline";
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          Admin Dashboard
        </h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Users</CardTitle>
          <CardDescription>
            Complete list of registered users on the platform
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {/* Mobile Card View */}
          <div className="md:hidden space-y-4 p-4">
            {users?.map((user) => (
              <div
                key={user.id}
                className="bg-gray-50 rounded-lg p-4 space-y-2"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {user.full_name || "N/A"}
                    </h3>
                    <p className="text-sm text-gray-600">{user.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={getUserTypeBadgeVariant(user.user_type)}>
                      {getUserTypeLabel(user.user_type)}
                    </Badge>
                    {user.user_type === "breeder" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenTierModal(user)}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {user.user_type === "breeder" && user.business_name && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Business:</span>{" "}
                    {user.business_name}
                  </p>
                )}

                {user.user_type === "breeder" && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Tier:</span>{" "}
                    <Badge variant={getTierBadgeVariant(user.admin_tier)}>
                      {getTierLabel(user.admin_tier)}
                    </Badge>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <Badge
                    variant={getSubscriptionBadgeVariant(
                      user.user_type,
                      user.subscription_active
                    )}
                  >
                    {getSubscriptionStatus(
                      user.user_type,
                      user.subscription_active
                    )}
                  </Badge>
                  <span className="text-xs text-gray-500">
                    Joined {formatDate(user.user_created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Full Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Business Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Subscription</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users?.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.full_name || "N/A"}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.user_type === "breeder"
                        ? user.business_name || "N/A"
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getUserTypeBadgeVariant(user.user_type)}>
                        {getUserTypeLabel(user.user_type)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.user_type === "breeder" ? (
                        <Badge variant={getTierBadgeVariant(user.admin_tier)}>
                          {getTierLabel(user.admin_tier)}
                        </Badge>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getSubscriptionBadgeVariant(
                          user.user_type,
                          user.subscription_active
                        )}
                      >
                        {getSubscriptionStatus(
                          user.user_type,
                          user.subscription_active
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {formatDate(user.user_created_at)}
                    </TableCell>
                    <TableCell>
                      {user.user_type === "breeder" ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenTierModal(user)}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {users && users.length === 0 && (
            <div className="text-center py-8 text-gray-500">No users found</div>
          )}
        </CardContent>
      </Card>

      {selectedUser && (
        <AccountTierModal
          isOpen={tierModalOpen}
          onClose={handleCloseTierModal}
          userId={selectedUser.id}
          currentTier={selectedUser.admin_tier}
          userName={selectedUser.full_name || selectedUser.email}
        />
      )}
    </div>
  );
};
